import type {Dispatch, SetStateAction} from 'react';
import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import type {PreviewStreamController} from '@pexip/media';
import {DeniedDevices, UserMediaStatus} from '@pexip/media';
import {pipe} from '@pexip/utils';

import type {
    DeviceError,
    DeviceErrors,
    DeniedDevice,
    DeviceStatusInfo,
} from '../types';

import {useDeviceStatusInfo} from './useDeviceStatusInfo';

export const useDeviceErrorMessageState = () => {
    const [videoInputError, setVideoInputError] = useState<DeviceError>({
        title: '',
    });
    const [audioInputError, setAudioInputError] = useState<DeviceError>({
        title: '',
    });

    return {
        videoInputError,
        setVideoInputError,
        audioInputError,
        setAudioInputError,
    };
};

export const useSubscribeToInputError = (
    setAudioInputError: Dispatch<SetStateAction<DeviceError>>,
    setVideoInputError: Dispatch<SetStateAction<DeviceError>>,
    controller?: PreviewStreamController,
) => {
    const {t} = useTranslation();
    const audioInputErrorTitle = t(
        'media.error.unable-to-use.audio',
        'Cannot use audio input device. Please select another one.',
    );
    const videoInputErrorTitle = t(
        'media.error.unable-to-use.video',
        'Cannot use video input device. Please select another one.',
    );
    useEffect(() => {
        return controller?.onAudioInputError(() => {
            setAudioInputError({
                title: audioInputErrorTitle,
            });
        });
    }, [controller, setAudioInputError, audioInputErrorTitle]);

    useEffect(() => {
        return controller?.onVideoInputError(() => {
            setVideoInputError({
                title: videoInputErrorTitle,
            });
        });
    }, [controller, setVideoInputError, videoInputErrorTitle]);
};

export const buildErrorMessages = (
    deviceStatusInfo: DeviceStatusInfo,
    deviceErrors: DeviceErrors = {
        videoError: {
            title: '',
            description: '',
        },
        audioError: {
            title: '',
            description: '',
        },
    },
): DeviceErrors => {
    const {videoError, audioError} = deviceErrors;

    if (deviceStatusInfo.distinctiveTitles) {
        videoError.title = deviceStatusInfo.distinctiveTitles.videoError.title;
        videoError.description =
            deviceStatusInfo.distinctiveTitles.videoError.description;
        audioError.title = deviceStatusInfo.distinctiveTitles.audioError.title;
        audioError.description =
            deviceStatusInfo.distinctiveTitles.audioError.description;
    } else {
        if (deviceStatusInfo.error === 'video') {
            videoError.title = deviceStatusInfo.title;
            videoError.description = deviceStatusInfo.message;
        } else if (deviceStatusInfo.error === 'audio') {
            audioError.title = deviceStatusInfo.title;
            audioError.description = deviceStatusInfo.message;
        }
    }

    return {videoError, audioError};
};

const DENIED_DEVICE_STATUS_MAP = new Map([
    [UserMediaStatus.PermissionsRejected, DeniedDevices.Both],
    [UserMediaStatus.PermissionsRejectedAudioInput, DeniedDevices.Microphone],
    [UserMediaStatus.PermissionsRejectedVideoInput, DeniedDevices.Camera],
    [UserMediaStatus.PermissionsOnlyAudioinput, DeniedDevices.Camera],
    [UserMediaStatus.PermissionsOnlyAudioinputFallback, DeniedDevices.Camera],
    [UserMediaStatus.PermissionsOnlyVideoinput, DeniedDevices.Microphone],
    [
        UserMediaStatus.PermissionsOnlyVideoinputFallback,
        DeniedDevices.Microphone,
    ],
]);

const addDeniedDevice =
    (streamStatus: UserMediaStatus) =>
    (deviceErrors: DeviceErrors): DeviceErrors => {
        const {videoError, audioError} = deviceErrors;

        const error = DENIED_DEVICE_STATUS_MAP.get(streamStatus);
        switch (error) {
            case DeniedDevices.Microphone:
                audioError.deniedDevice = DeniedDevices.Microphone;
                break;
            case DeniedDevices.Camera:
                videoError.deniedDevice = DeniedDevices.Camera;
                break;
            case DeniedDevices.Both:
                audioError.deniedDevice = DeniedDevices.Both;
                videoError.deniedDevice = DeniedDevices.Both;
                break;
        }

        return {videoError, audioError};
    };

export const getDeviceErrorMessageSetter =
    (
        deviceStatusInfo: DeviceStatusInfo,
        setAudioInputError: Dispatch<SetStateAction<DeviceError>>,
        setVideoInputError: Dispatch<SetStateAction<DeviceError>>,
    ) =>
    (deviceErrors: DeviceErrors) => {
        const {videoError, audioError} = deviceErrors;

        const setVideoError = () => setVideoInputError(videoError);
        const setAudioError = () => setAudioInputError(audioError);

        const setError: Record<DeniedDevice, () => void> = {
            ['audio']: setAudioError,
            ['video']: setVideoError,
            ['audio-video']: () => {
                setAudioError();
                setVideoError();
            },
        };

        if (deviceStatusInfo.error) {
            return setError[deviceStatusInfo.error];
        }
    };

/**
 * Sets `DeviceError` when device is missing or its permissions are rejected.
 */
export const useDeviceErrorMessage = ({
    setAudioInputError,
    setVideoInputError,
    streamStatus,
    requested,
}: {
    setAudioInputError: Dispatch<SetStateAction<DeviceError>>;
    setVideoInputError: Dispatch<SetStateAction<DeviceError>>;
    streamStatus: UserMediaStatus;
    requested: {audio: boolean; video: boolean};
}) => {
    const deviceStatusInfo = useDeviceStatusInfo(streamStatus, {
        audio: requested.audio,
        video: requested.video,
    });

    useEffect(() => {
        const pipeline = pipe(
            buildErrorMessages,
            addDeniedDevice(streamStatus),
        );

        const {videoError, audioError} = pipeline(deviceStatusInfo);

        const setErrorMessage = getDeviceErrorMessageSetter(
            deviceStatusInfo,
            setAudioInputError,
            setVideoInputError,
        )({videoError, audioError});

        setErrorMessage?.();

        return () => {
            setAudioInputError({
                title: '',
            });
            setVideoInputError({
                title: '',
            });
        };
    }, [
        deviceStatusInfo,
        setAudioInputError,
        setVideoInputError,
        streamStatus,
    ]);
};
